import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { FilterDropdown } from '@/components/search/FilterDropdown'
import styles from '@/components/search/search.module.scss'
import { FILTER_SPEC } from '@/lib/constants'
import { selectFullActiveSpec, selectSpecSearchFilter } from '@/store/selectors/search'
import { SearchFilterSpec, SearchFilterSpecSubFilter } from '@/types/search'
import { Size } from '@/types/system'
import { isProd } from '@/lib/utilities/system'

const Icon = dynamic(() => import('@/components/styleguide/Icon'))

type AlloysProps = {
  isFilterOpen: boolean
  toggleFilter: () => void
  onChange: (params: {
    type: 'multi' | 'single' | 'vehiclePriceTag'
    filter: string
    value: string | number
  }) => void
}

export const Alloys = ({ isFilterOpen, toggleFilter, onChange }: AlloysProps) => {
  const availableSpec = useSelector(selectSpecSearchFilter)
  const selectedSpec = useSelector(selectFullActiveSpec)
  const [availableAlloys, setAvailableAlloys] = useState<SearchFilterSpec[]>([])
  const [selectedAlloys, setSelectedAlloys] = useState<SearchFilterSpecSubFilter | undefined>(
    undefined,
  )

  useEffect(() => {
    setAvailableAlloys(
      availableSpec.filter((spec: SearchFilterSpec) => spec.name === 'Alloys')[0]?.subFilters || [],
    )
  }, [availableSpec])

  useEffect(() => {
    setSelectedAlloys(
      selectedSpec.filter(
        (spec: { id: number; name: string; filter: string }) => spec.filter === 'Alloys',
      )[0],
    )
  }, [selectedSpec])

  const onChangeSelect = (event: React.FormEvent<HTMLSelectElement>) => {
    if (selectedAlloys) {
      const selectedId = availableAlloys.filter(
        (available: SearchFilterSpecSubFilter) => selectedAlloys.id === available.id,
      )[0].id

      onChange({
        type: 'multi',
        value: selectedId,
        filter: FILTER_SPEC,
      })
    }

    if (event.currentTarget.value === '') {
      setSelectedAlloys(undefined)
    } else {
      onChange({
        type: 'multi',
        value: parseInt(event.currentTarget.value),
        filter: FILTER_SPEC,
      })

      setSelectedAlloys(
        availableAlloys.filter(
          (available: SearchFilterSpecSubFilter) =>
            parseInt(event.currentTarget.value) === available.id,
        )[0],
      )
    }
  }

  return (
    <FilterDropdown
      title="Alloys"
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={!!selectedAlloys}
      selectedOptions={selectedAlloys ? [`Minimum ${selectedAlloys.name} inch`] : ['All']}>
      {availableAlloys.length > 0 ? (
        <div className={styles.select}>
          <select
            onChange={onChangeSelect}
            defaultValue=""
            value={selectedAlloys?.id || ''}
            className={`${styles.control}${!isProd() ? ` search__performance-select-alloys` : ''}`}
            title="Alloys"
            name="alloys">
            <option
              value=""
              label="Any"
            />
            {availableAlloys.length > 0 &&
              availableAlloys.map((item: SearchFilterSpecSubFilter) => {
                return (
                  <option
                    key={item.id}
                    value={item.id}>
                    Minimum {item.name} inch
                  </option>
                )
              })}
          </select>
          <Icon
            customClass={styles.icon}
            name="ChevronDown"
            size={Size.Medium}
          />
        </div>
      ) : (
        <p className={styles['error-text']}>
          {'There are no "Alloys" options available based on your current filter selections'}
        </p>
      )}
    </FilterDropdown>
  )
}
